import { act, useEffect, useRef, useState } from "react";
import API from "../axios";
import { Col, Container, Row } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import MatchSpecificationVehicle from "../components/MatchSpecificationVehicle";
import { arrayMove } from "@dnd-kit/sortable";
import VehicleWheelOptions from "../components/VehicleWheelOptions";
import ColorAttributeValues from "../components/ColorAttributeValues";
import ImageAttributeValues from "../components/ImageAttributeValues";
import VehicleVariants from "../components/VehicleVariants";

const VehicleDetail = () => {
    const navigate = useNavigate();

    const [saved, setSaved] = useState(false);
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [chargerPhotoUploading, setChargerPhotoUploading] = useState(false);
    const [productPhotoUploading, setProductPhotoUploading] = useState(false);
    const [vehicle, setVehicle] = useState<any>();
    const [saveLoading, setSaveLoading] = useState(false);

    // Form fields
    const [odooId, setOdooId] = useState("");
    const [title, setTitle] = useState("");
    const [productShortDescription, setProductShortDescription] = useState("");
    const [productDescription, setProductDescription] = useState("");
    const [active, setActive] = useState("");
    const [isAppleCarPlayAvailable, setIsAppleCarPlayAvailable] = useState("");

    // Pricing
    const [regularPrice, setRegularPrice] = useState("");
    const [discountPrice, setDiscountPrice] = useState("");
    const [factoryPrice, setFactoryPrice] = useState("");

    // Photos
    const [productImage, setProductImage] = useState<any>("");
    const [chargerImage, setChargerImage] = useState<any>("");
    const [productGallery, setProductGallery] = useState<any>([]);
    const [currentUpload, setCurrentUpload] = useState("product");

    // Specification
    const [specification, setSpecification] = useState<any>(null);

    // Variants
    const [variants, setVariants] = useState([]);
    const [vehicleVariants, setVehicleVariants] = useState([]);

    const tabs = ["Vehicle Price", "Variant List", "Specification"];
    const [activeTab, setActiveTab] = useState(0);

    const fileUploadRef = useRef<any>(null);
    const chargerUploadRef = useRef<any>(null);

    const loadVehicle = async function () {
        setLoading(true);

        const result: any = await API.getOneVehicle(id as string);

        // Vehicle info
        setOdooId(result.data.odooId);
        setTitle(result.data.title);
        setProductShortDescription(result.data.productShortDescription);
        setProductDescription(result.data.productDescription);
        setProductImage(result.data.productImage);
        setChargerImage(result.data.chargerImage);
        setProductGallery(result.data.productGallery);
        setActive(result.data.active ? "true" : "false");
        setIsAppleCarPlayAvailable(
            result.data.isAppleCarPlayAvailable ? "true" : "false1",
        );

        // Vehicle price
        setRegularPrice(result.data.defaultPrice);
        setDiscountPrice(result.data.discountPrice);
        setFactoryPrice(result.data.factoryPrice);
        setVehicle(result.data);
        setSpecification(result.data.specification?.specification || null);

        // Variants
        setVariants(result.data.productAttributes);

        const variantResult: any = await API.paginateVehicleVariants({
            vehicle: id,
        });
        setVehicleVariants(variantResult.data);

        setLoading(false);
    };

    const handleFileChange = async (e: any) => {
        const files = e.target.files;

        if (!files || files.length === 0) return;

        const file = files[0]; // Only one allowed
        setProductPhotoUploading(true);

        const result: any = await API.upload(file, "xcess.com/vehicles");
        setProductImage(result.fileUrl);

        setProductPhotoUploading(false);
    };

    const handleChargerPhotoChange = async (e: any) => {
        const files = e.target.files;

        if (!files || files.length === 0) return;

        const file = files[0];
        setChargerPhotoUploading(true);

        const result: any = await API.upload(file, "xcess.com/cherger");
        setChargerImage(result.fileUrl);

        setChargerPhotoUploading(false);
    };

    const handleSave = async function () {
        setSaveLoading(true);

        const data = {
            title,
            productShortDescription,
            productDescription,
            regularPrice: !!regularPrice ? parseFloat(regularPrice) : null,
            discountPrice: !!discountPrice ? parseFloat(discountPrice) : null,
            factoryPrice: !!factoryPrice ? parseFloat(factoryPrice) : null,
            productImage,
            chargerImage,
            productGallery,
            active: active === "true",
            isAppleCarPlayAvailable: isAppleCarPlayAvailable == "true",
        };

        await API.updateVehicle(id as string, data);

        if (specification && Object.keys(specification).length > 0) {
            await API.saveSpecification(id as string, specification);
        }

        setSaved(true);
        setSaveLoading(false);
    };

    useEffect(() => {
        loadVehicle();
    }, []);

    useEffect(() => {
        setTimeout(() => {
            setSaved(false);
        }, 3000);
    }, [saved]);

    return (
        <div>
            {loading ? (
                <div
                    className="d-flex justify-content-center my-5"
                    style={{ height: "100%" }}
                >
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : (
                <Container style={{ marginTop: 100, paddingBottom: 200 }}>
                    {/* Header */}
                    <Row className="mt-5">
                        <Col
                            cols="6"
                            className="d-flex align-items-center gap-2"
                        >
                            <button
                                className="btn btn-outline-black btn-md mx-1"
                                onClick={() => navigate(-1)}
                            >
                                <i className="bi bi-chevron-left fs-4"></i>
                            </button>
                            <h3 className="fw-medium text-primary">
                                Edit Vehicle
                            </h3>
                        </Col>
                        <Col
                            cols="6"
                            className="text-end d-flex justify-content-end gap-2"
                        >
                            <a
                                type="button"
                                className="btn btn-primary d-flex align-items-center"
                                href={`/edit-vehicle-cms/${odooId}`}
                            >
                                <i className="bi bi-pencil mx-2"></i>
                                Edit CMS
                            </a>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSave}
                                disabled={saveLoading}
                            >
                                <i className="bi bi-save mx-2"></i>
                                {saveLoading ? "Saving..." : "Save"}
                            </button>
                            {saved && (
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    disabled
                                >
                                    <i className="bi bi-check mx-2"></i>
                                    Saved
                                </button>
                            )}
                        </Col>
                    </Row>
                    {/* Vehicle Info */}
                    <Row className="mt-5">
                        <Col xs="12" md="8" style={{ zIndex: 100 }}>
                            <div className="py-3">
                                <label className="form-label fw-bold">
                                    OdooId
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter product name"
                                    value={odooId}
                                    onChange={(e) => setOdooId(e.target.value)}
                                    disabled
                                />
                            </div>
                            <div className="py-3">
                                <label className="form-label fw-bold">
                                    Vehicle Name
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter product name"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    disabled
                                />
                            </div>
                            <div className="py-3">
                                <label className="form-label fw-bold">
                                    Short Description
                                </label>
                                <textarea
                                    className="form-control"
                                    name="short-descripton"
                                    placeholder="Enter short description"
                                    rows={3}
                                    value={productShortDescription}
                                    onChange={(e) =>
                                        setProductShortDescription(
                                            e.target.value,
                                        )
                                    }
                                ></textarea>
                            </div>
                            <div className="py-3">
                                <label className="form-label fw-bold">
                                    Description
                                </label>
                                <textarea
                                    className="form-control"
                                    name="short-descripton"
                                    placeholder="Enter short description"
                                    rows={7}
                                    value={productDescription}
                                    onChange={(e) =>
                                        setProductDescription(e.target.value)
                                    }
                                ></textarea>
                            </div>
                            <div className="py-3">
                                <label className="form-label">Active</label>
                                <select
                                    className="form-select"
                                    value={active}
                                    onChange={(e) => {
                                        if (e.target.value === "true" && !vehicle?.specification?.audit_by) return;
                                        setActive(e.target.value);
                                    }}
                                >
                                    <option value="">-- Select --</option>
                                    <option
                                        value="true"
                                        disabled={!vehicle?.specification?.audit_by}
                                    >
                                        Public{!vehicle?.specification?.audit_by ? " (spec not audited)" : ""}
                                    </option>
                                    <option value="false">Hidden</option>
                                </select>
                                {!vehicle?.specification?.audit_by && (
                                    <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>
                                        Specification must be audited before the vehicle can be made public.
                                    </div>
                                )}
                            </div>

                            <div className="py-3">
                                <label className="form-label">
                                    Is Apple Car Play Available ?
                                </label>
                                <select
                                    className="form-select"
                                    value={isAppleCarPlayAvailable}
                                    onChange={(e) =>
                                        setIsAppleCarPlayAvailable(
                                            e.target.value,
                                        )
                                    }
                                >
                                    <option value="">-- Select --</option>
                                    <option value="true">Available</option>
                                    <option value="false">Not Available</option>
                                </select>
                            </div>

                            <div className="divider"></div>

                            <ul className="nav nav-tabs mt-5">
                                {tabs.map((tab, index) => (
                                    <li
                                        key={tab}
                                        className="nav-item"
                                        onClick={() => setActiveTab(index)}
                                        style={{
                                            cursor: "pointer",
                                        }}
                                    >
                                        <a
                                            className={`nav-link ${
                                                index == activeTab
                                                    ? "active"
                                                    : ""
                                            }`}
                                            aria-current="page"
                                            style={{
                                                fontSize: 12,
                                                fontWeight: "500",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 5,
                                            }}
                                        >
                                            {tab}
                                            {index === 2 && vehicle?.specification?.audit_by && (
                                                <span
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        width: 15,
                                                        height: 15,
                                                        borderRadius: "50%",
                                                        background: "#16a34a",
                                                        color: "#fff",
                                                        fontSize: 9,
                                                        fontWeight: 700,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    ✓
                                                </span>
                                            )}
                                        </a>
                                    </li>
                                ))}
                                {vehicle?.productAttributes.map(
                                    (productAttribute: any, index: number) => (
                                        <li
                                            key={index}
                                            className="nav-item"
                                            onClick={() =>
                                                setActiveTab(
                                                    // `${index}_${productAttribute.attributeName}`
                                                    index + 3,
                                                )
                                            }
                                            style={{
                                                cursor: "pointer",
                                            }}
                                        >
                                            <a
                                                className={`nav-link ${
                                                    index + 3 == activeTab
                                                        ? "active"
                                                        : ""
                                                }`}
                                                aria-current="page"
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: "500",
                                                }}
                                            >
                                                {productAttribute.attributeName}
                                            </a>
                                        </li>
                                    ),
                                )}
                            </ul>

                            {/* Price */}
                            {activeTab == 0 && (
                                <Row className="mt-3">
                                    <Col xs="6">
                                        <div className="py-3">
                                            <label className="form-label fw-bold">
                                                Regular Price
                                            </label>
                                            <br />
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder="Enter price"
                                                value={regularPrice}
                                                disabled
                                            />
                                            <small className="text-dark-subtle">
                                                The regular is used for normal
                                                price without discount or any
                                                other conditions.
                                            </small>
                                        </div>
                                    </Col>
                                    <Col xs="6">
                                        <div className="py-3">
                                            <label className="form-label fw-bold">
                                                Discount Price
                                            </label>
                                            <br />
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder="Enter price"
                                                value={discountPrice}
                                                disabled
                                            />
                                            <small className="text-dark-subtle">
                                                Discount price will calcuate
                                                with when the value is not
                                                empty.
                                            </small>
                                        </div>
                                    </Col>
                                    <Col xs="6">
                                        <div className="py-3">
                                            <label className="form-label fw-bold">
                                                Factory Price
                                            </label>
                                            <br />
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder="Enter price"
                                                value={factoryPrice}
                                                disabled
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            {activeTab == 1 && (
                                <VehicleVariants vehicleId={id as string} />
                            )}

                            {activeTab == 2 && (
                                <>
                                    {vehicle?.specification?.audit_by ? (
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 12,
                                                padding: "10px 14px",
                                                background: "#f0fdf4",
                                                border: "1px solid #bbf7d0",
                                                borderRadius: 8,
                                                marginTop: 16,
                                                marginBottom: 4,
                                            }}
                                        >
                                            <i
                                                className="bi bi-patch-check-fill"
                                                style={{
                                                    color: "#16a34a",
                                                    fontSize: 18,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <div>
                                                <div
                                                    style={{
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        textTransform:
                                                            "uppercase",
                                                        color: "#16a34a",
                                                        letterSpacing: 0.5,
                                                    }}
                                                >
                                                    Audited
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: 12,
                                                        color: "#374151",
                                                    }}
                                                >
                                                    {vehicle.specification
                                                        .audit_by}
                                                </div>
                                            </div>
                                            {vehicle.specification
                                                .audit_sign && (
                                                <img
                                                    src={
                                                        vehicle.specification
                                                            .audit_sign
                                                    }
                                                    alt="Audit signature"
                                                    style={{
                                                        height: 40,
                                                        marginLeft: "auto",
                                                        border: "1px solid #bbf7d0",
                                                        borderRadius: 6,
                                                        background: "#fff",
                                                    }}
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8,
                                                padding: "8px 12px",
                                                background: "#fef9c3",
                                                border: "1px solid #fde047",
                                                borderRadius: 8,
                                                marginTop: 16,
                                                marginBottom: 4,
                                                fontSize: 12,
                                                color: "#713f12",
                                            }}
                                        >
                                            <i className="bi bi-exclamation-triangle" />
                                            Not yet audited
                                        </div>
                                    )}
                                    <MatchSpecificationVehicle
                                        vehicleId={id as string}
                                        specification={
                                            vehicle.specification?.specification
                                        }
                                        onSpecificationChange={(spec: any) =>
                                            setSpecification(spec)
                                        }
                                    />
                                </>
                            )}

                            {activeTab == 3 && (
                                <ColorAttributeValues
                                    datas={
                                        vehicle.productAttributes[0]
                                            .attributeValues
                                    }
                                    handleColorUpdated={() => {}}
                                />
                            )}

                            {activeTab == 4 && (
                                <ColorAttributeValues
                                    datas={
                                        vehicle.productAttributes[1]
                                            .attributeValues
                                    }
                                    handleColorUpdated={() => {}}
                                />
                            )}

                            {activeTab > 4 && (
                                <ImageAttributeValues
                                    key={activeTab}
                                    attribute={
                                        vehicle.productAttributes[activeTab - 3]
                                    }
                                    datas={
                                        vehicle.productAttributes[activeTab - 3]
                                            .attributeValues
                                    }
                                    handleImageUpdated={() => {}}
                                    handlePriceUpdated={() => {}}
                                    vehicleId={vehicle._id}
                                    vehicleOdooId={vehicle.odooId}
                                    index={activeTab - 3}
                                    handleHiddenUpdatd={() => loadVehicle()}
                                />
                            )}
                        </Col>
                        <Col xs="12" md="4" className="p-y-2">
                            <div>
                                <div className="d-flex flex-row justify-content-between">
                                    <label className="form-label fw-bold">
                                        Product Photo
                                    </label>
                                    <button
                                        type="button"
                                        className="btn btn-grey btn-sm mx-1"
                                        onClick={() => setProductImage("")}
                                    >
                                        <i className="bi bi-trash"></i>
                                    </button>
                                </div>
                                {productImage ? (
                                    <img
                                        src={productImage}
                                        width="100%"
                                        className="rounded-2"
                                    />
                                ) : (
                                    <>
                                        <div
                                            className="bg-body-secondary rounded-2 d-flex justify-content-center align-items-center"
                                            style={{
                                                width: "100%",
                                                height: 250,
                                                cursor: "pointer",
                                            }}
                                            onClick={() => {
                                                fileUploadRef.current?.click();
                                                setCurrentUpload("product");
                                            }}
                                        >
                                            <i className="bi bi-upload mx-3"></i>
                                            <small>
                                                {productPhotoUploading
                                                    ? "Uploading ..."
                                                    : "Upload new photo"}
                                            </small>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div>
                                <div className="d-flex flex-row justify-content-between mt-3">
                                    <label className="form-label fw-bold">
                                        Charger Photo
                                    </label>
                                    <button
                                        type="button"
                                        className="btn btn-grey btn-sm mx-1"
                                        onClick={() => setChargerImage("")}
                                    >
                                        <i className="bi bi-trash"></i>
                                    </button>
                                </div>
                                {chargerImage ? (
                                    <img
                                        src={chargerImage}
                                        width="100%"
                                        className="rounded-2"
                                    />
                                ) : (
                                    <>
                                        <div
                                            className="bg-body-secondary rounded-2 d-flex justify-content-center align-items-center"
                                            style={{
                                                width: "100%",
                                                height: 250,
                                                cursor: "pointer",
                                            }}
                                            onClick={() => {
                                                chargerUploadRef.current?.click();
                                            }}
                                        >
                                            <i className="bi bi-upload mx-3"></i>
                                            <small>
                                                {chargerPhotoUploading
                                                    ? "Uploading ..."
                                                    : "Upload new photo"}
                                            </small>
                                        </div>
                                    </>
                                )}
                            </div>
                        </Col>
                        <Col cols="12"></Col>
                    </Row>
                </Container>
            )}

            <input
                type="file"
                ref={fileUploadRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
            />
            <input
                type="file"
                ref={chargerUploadRef}
                onChange={handleChargerPhotoChange}
                style={{ display: "none" }}
            />
        </div>
    );
};

export default VehicleDetail;
